/**
 * 後端 BaseResponse 的 TypeScript 型別。
 * -----------------------------------------------------------------------------
 * 對齊 `api-service/model/response/base_response.py`：所有 endpoint 都用
 * `{ success, error, result, access_token }` 包一層，前端 HTTP adapter 解開後
 * 再交給上層應用，呼叫端只看到 `result`。
 */

/**
 * 後端統一錯誤資訊。
 *
 * @example
 *   { code: 'AUTH_INVALID_TOKEN', message: 'Invalid or expired token' }
 *   { code: 422, message: 'Validation error' }
 *   { code: 'ORDER_NOT_CANCELLABLE', message: '...', reasonCode: 'CREDITS_USED' }
 */
export interface ApiError {
  code: string | number;
  message: string;
  /**
   * Billing 取消/替換等 endpoint 的細分原因。
   *
   * 後端 Q5:wire 端在 envelope `error.reason_code`(snake);transport 邊界
   * `normalizeEnvelopeError`(httpApiClient)映射成此 camel `reasonCode`。caller 端
   * 一律走白名單驗證(`features/billing/errorMap.getCancelReasonCode`)再使用。
   */
  reasonCode?: string;
  /**
   * R135(docs/75 v2.3 §8.1)— 目前只有 `PRESET_INPUT_INVALID` 帶:
   * `{ node_id, tool_type, field_errors: [{field, reason, message}] }`(肇事影片卡 +
   * 逐欄位錯誤)。其他錯誤碼 envelope **沒有**此 key。runtime 由
   * `normalizeEnvelopeError` spread 透傳;型別守衛走
   * `video-short/videoShort.parsePresetInputInvalidDetails`,不要直接 cast。
   */
  details?: unknown;
  /**
   * 工作區數量上限(只有 `WORKSPACE_QUOTA_EXCEEDED` 才帶;docs/47 §0.1/§3.E)。
   *
   * wire 端在 envelope `error.limit`;transport 邊界 `normalizeEnvelopeError` 直接
   * 透傳(camel 與 snake 同名,無需改名)。caller 顯示上限文案免 hardcode。
   */
  limit?: number;
}

/**
 * 後端 `BaseResponse` 外殼。
 *
 * `result` 型別由各 endpoint 自行決定;錯誤時 `error` 會帶值,`success=false`。
 *
 * **Round 50(R45+)**:envelope `{ success, error, result }` 三個 field;`access_token`
 * 已砍(R42.5 對齊 backend MR !26;token 永遠走 httpOnly cookie)。新 BE endpoint
 * 可能只回 `{result}` 不帶 `success` field(httpApiClient 視 undefined 為成功)。
 *
 * @example
 *   const ok: BaseResponse<{ ping: string }> = {
 *     success: true,
 *     error: null,
 *     result: { ping: 'pong' },
 *   };
 */
export interface BaseResponse<TResult = unknown> {
  success: boolean;
  error: ApiError | null;
  result: TResult | null;
}

/**
 * 通用「清單」回應信封 —— `result` 為 `{ items: T[] }` 的 endpoint 共用。
 *
 * groups / uploads / projects / brandKits 的 list endpoint 都回此 shape;放在
 * `api.ts` 與 :type:`BaseResponse` 同層,避免綁定特定領域檔(原本誤放
 * `uploads.ts` 叫 `UploadedListDto`,R-refactor 升格中性名移來這)。
 *
 * **不涵蓋 cursor 分頁清單**(如 assets list 的 `{ assets, nextCursor }`)——
 * 那種 shape 不同,各自定型別。
 *
 * @example
 *   const env: BaseResponse<ListResultDto<GroupEntityDto>> = {
 *     success: true,
 *     error: null,
 *     result: { items: [...] },
 *   };
 */
export interface ListResultDto<TItem> {
  items: TItem[];
}

/**
 * HTTP adapter 解到非 2xx 或 `success=false` 時丟出的例外。
 *
 * 呼叫端可用 `instanceof ApiHttpError` 篩出「業務錯誤」與真正的網路 / 程式錯誤。
 *
 * @example
 *   try {
 *     await services.authProvider.loginStatus();
 *   } catch (e) {
 *     if (e instanceof ApiHttpError && e.error?.code === 'AUTH_NOT_AUTHENTICATED') {
 *       // 顯示登入頁
 *     }
 *   }
 */
export class ApiHttpError extends Error {
  readonly status: number;
  readonly error: ApiError | null;

  constructor(status: number, error: ApiError | null, message?: string) {
    super(message ?? error?.message ?? `HTTP ${status}`);
    this.name = 'ApiHttpError';
    this.status = status;
    this.error = error;
  }
}

/**
 * R286 — 這個錯誤是不是「打太多太快」的限流(HTTP 429)。
 *
 * 畫布上節點/縮圖一多,簽 URL 等請求併發就會撞 BE 限流(如 download-url 60/min),
 * 而 `ApiHttpError.message` 的 fallback 是 `HTTP 429` —— 任何把 raw message 內插進
 * 文案的呈現點都會把「429」裸露給使用者。判準收一份,呈現端一律顯
 * `rateLimited` 文案(「載入太多太快,系統限制中…」),不要各自 `status === 429`。
 */
export function isRateLimitedError(err: unknown): boolean {
  return err instanceof ApiHttpError && err.status === 429;
}
